import React, { useMemo, useState } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import { DAILY_ENTRY_CONTEXT } from './daily-entry-context.js';
import DailyEntryContextRail from './DailyEntryContextRail.jsx';
import DailyEntryRoster from './DailyEntryRoster.jsx';
import DailyEntryActionPanel from './DailyEntryActionPanel.jsx';
import { todayIsoDate } from './daily-entry-ui.js';
import { useDailyEntryRoster } from './use-daily-entry-roster.js';
import { useDailyEntryActions } from './use-daily-entry-actions.js';
import DailyEntryScorecard from './DailyEntryScorecard.jsx';
import DailyEntryDSWUploadDrawer from './DailyEntryDSWUploadDrawer.js';
import { useDiagMode } from '../../lib/use-diag-mode.js';

const TERMINAL_SEED = [
  { id: 'all', name: 'All terminals' },
  { id: 'lex-01', name: 'Lexington Terminal' },
  { id: 'shv-01', name: 'Shreveport Terminal' },
];

const DAILY_ENTRY_SEED = [
  { id: 'context', label: 'Context rail', status: 'complete' },
  { id: 'roster', label: 'Roster read surface', status: 'complete' },
  { id: 'entry-state', label: 'Entry/clock read-state overlay', status: 'complete' },
  { id: 'worked-shell', label: 'Worked action shell', status: 'complete' },
  { id: 'absent-shell', label: 'Absent action shell', status: 'complete' },
  { id: 'absent-write', label: 'Absent submit path', status: 'complete' },
  { id: 'worked-form', label: 'Worked form skeleton', status: 'complete' },
  { id: 'worked-write', label: 'Minimal worked submit path', status: 'complete' },
  { id: 'success-reset', label: 'Success/reset cleanup', status: 'complete' },
];

export default function DailyEntryModule() {
  const [diagEnabled] = useDiagMode();
  const [selectedTerminalId, setSelectedTerminalId] = useState('');
  const [selectedDate, setSelectedDate] = useState(todayIsoDate());
  const [showDSWUpload, setShowDSWUpload] = useState(false);
  const {
    attendanceByEmployee,
    clockByEmployee,
    roster,
    terminals,
    loading,
    error,
    search,
    statusFilter,
    setSearch,
    setStatusFilter,
    refreshRoster,
  } = useDailyEntryRoster(selectedDate, selectedTerminalId);

  const {
    selectedDriver,
    activeAction,
    clockStatus,
    submitting,
    actionError,
    successMessage,
    absenceReason,
    absenceNotes,
    workedForm,
    setWorkedForm,
    setAbsenceReason,
    setAbsenceNotes,
    openWorked,
    openAbsent,
    openEdit,
    submitWorked,
    submitAbsent,
    submitEdit,
    closeAction,
  } = useDailyEntryActions(selectedDate, refreshRoster);

  const derivedTerminals = useMemo(() => {
    if (terminals.length) {
      return terminals.map((terminal: { id: string | number; name?: string; code?: string; fedex_code?: string }) => ({
        id: terminal.id,
        name: terminal.name || terminal.code || terminal.fedex_code || `Terminal ${terminal.id}`,
      }));
    }

    const seen = new Map();
    roster.forEach((driver) => {
      const id = driver.assigned_terminal_id;
      if (!id || seen.has(String(id))) return;
      seen.set(String(id), {
        id,
        name: driver.assigned_terminal_name || driver.terminal_name || `Terminal ${id}`,
      });
    });
    return seen.size ? Array.from(seen.values()) : TERMINAL_SEED;
  }, [roster, terminals]);

  const selectedTerminal = useMemo(
    () => derivedTerminals.find((terminal) => String(terminal.id) === String(selectedTerminalId)) || null,
    [derivedTerminals, selectedTerminalId],
  );

  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textMuted, marginBottom: 8 }}>
        Daily entry module boundary
      </div>
      <p style={{ marginTop: 0, lineHeight: 1.6 }}>
        This is the first real Daily Entry chain on the V2 rail. Batch 1 is building the read/context substrate first so later write paths have a clean downstream foundation.
      </p>

      <DailyEntryContextRail
        terminals={derivedTerminals}
        selectedTerminalId={selectedTerminalId}
        selectedDate={selectedDate}
        onTerminalChange={setSelectedTerminalId}
        onDateChange={setSelectedDate}
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setShowDSWUpload(true)}
        >
          + Upload daily DSW
        </button>
      </div>

      <DailyEntryDSWUploadDrawer
        open={showDSWUpload}
        onClose={() => setShowDSWUpload(false)}
        onImported={() => { refreshRoster(); }}
      />

      <div style={LAYOUT.subPanel}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 8 }}>
          Current Daily Entry context
        </div>
        <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <div style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
            <div style={{ color: C.gold, fontSize: 14, marginBottom: 6 }}>terminal scope</div>
            <div style={{ color: C.textMuted, fontSize: 12 }}>{selectedTerminal ? selectedTerminal.name : 'All terminals'}</div>
          </div>
          <div style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
            <div style={{ color: C.gold, fontSize: 14, marginBottom: 6 }}>entry date</div>
            <div style={{ color: C.textMuted, fontSize: 12 }}>{selectedDate}</div>
          </div>
        </div>
      </div>

      {loading ? <div style={{ color: C.textMuted, marginTop: 16 }}>Loading Daily Entry roster...</div> : null}
      {error ? <div style={{ color: C.red, marginTop: 16 }}>Daily Entry load failed: {error}</div> : null}

      {!loading && !error ? (
        <DailyEntryRoster
          roster={roster}
          attendanceByEmployee={attendanceByEmployee}
          clockByEmployee={clockByEmployee}
          search={search}
          statusFilter={statusFilter}
          onSearchChange={setSearch}
          onStatusFilterChange={setStatusFilter}
          onWorked={openWorked}
          onAbsent={openAbsent}
          onEdit={openEdit}
        />
      ) : null}

      <DailyEntryActionPanel
        activeAction={activeAction}
        selectedDriver={selectedDriver}
        clockStatus={clockStatus}
        submitting={submitting}
        actionError={actionError}
        successMessage={successMessage}
        absenceReason={absenceReason}
        absenceNotes={absenceNotes}
        workedForm={workedForm}
        onWorkedFormChange={(field: string, value: string) => setWorkedForm((current) => ({ ...current, [field]: value }))}
        onAbsenceReasonChange={setAbsenceReason}
        onAbsenceNotesChange={setAbsenceNotes}
        onSubmitWorked={submitWorked}
        onSubmitAbsent={submitAbsent}
        onSubmitEdit={submitEdit}
        onClose={closeAction}
      />

      {!loading && !error && (
        <DailyEntryScorecard
          selectedDate={selectedDate}
          roster={roster}
          onRefresh={refreshRoster}
        />
      )}

      {diagEnabled && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 8 }}>
            Daily Entry chain targets
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            {DAILY_ENTRY_SEED.map((item) => (
              <div key={item.id} style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
                <div style={{ color: C.gold, fontSize: 14, marginBottom: 4 }}>{item.label}</div>
                <div style={{ color: C.textMuted, fontSize: 12 }}>status: {item.status}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {diagEnabled && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textMuted, marginBottom: 8 }}>
            Daily entry context
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
              <div style={{ color: C.gold, fontSize: 14, marginBottom: 6 }}>primary axes</div>
              <div style={{ color: C.textMuted, fontSize: 12 }}>{DAILY_ENTRY_CONTEXT.primaryAxes.join(', ')}</div>
            </div>
            <div style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
              <div style={{ color: C.gold, fontSize: 14, marginBottom: 6 }}>depends on</div>
              <div style={{ color: C.textMuted, fontSize: 12 }}>{DAILY_ENTRY_CONTEXT.dependsOn.join(', ')}</div>
            </div>
            <div style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
              <div style={{ color: C.gold, fontSize: 14, marginBottom: 6 }}>unlocks</div>
              <div style={{ color: C.textMuted, fontSize: 12 }}>{DAILY_ENTRY_CONTEXT.unlocks.join(', ')}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
